import { ReactNode, useCallback, useLayoutEffect } from 'react';
import { LoadingTracker } from 'src/components/common/LoadingTracker';
import { ActivePosEntityProvider } from 'src/contexts/ActivePosEntityContext';
import { ActiveSortTableColumnProvider } from 'src/contexts/ActiveSortTableColumnContext';
import { useAppContext } from 'src/contexts/AppContext';
import {
  CatalogDataContextProvider,
  ExpandedEventData,
} from 'src/contexts/CatalogDataContext';
import { CatalogMetricsContextProvider } from 'src/contexts/CatalogMetricsContext';
import { ErrorTypes, useClientLogContext } from 'src/contexts/ClientLogContext';
import { FilterQueryContextProvider } from 'src/contexts/FilterQueryContext';
import { SidePanelProvider } from 'src/contexts/SidePanelContext';
import { StopwatchLoggingContextProvider } from 'src/contexts/StopwatchLoggingContext';
import { useMatchMedia } from 'src/hooks/useMatchMedia';
import { usePageLoadStatus } from 'src/hooks/usePageLoadStatus';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import {
  getPresetFromUiDateTimeRange,
  getUiDateTimeRangeFromPreset,
  InhandDateRangePresetNames,
  StandardDateRangePresetNames,
} from 'src/utils/dateTimeUtils';
import { DefaultSaleQuery, EmptySaleQuery } from 'src/utils/eventQueryUtils';
import { transformData } from 'src/utils/eventWithDataUtils';
import { addSaleMetrics } from 'src/utils/ticketMetricUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ActionOutboxEntityType,
  CatalogClient,
  DateTimeRange,
  Feature,
  PosClientConfig,
  SaleClient,
  SaleDetails,
  SaleMetrics,
  SaleQuery,
  SaleQueryV2,
  UserSetting,
} from 'src/WebApiController';

import { SaleEventsMobile } from './mobile/SaleEventsMobile';
import { SalesEventsExplorer } from './SalesEventsExplorer';

export const SALE_USER_SETTINGS = [
  UserSetting.SaleColumnEditability,
  UserSetting.SaleColumnNumberPrecision,
  UserSetting.SaleColumnOrder,
  UserSetting.SaleColumnsEnabled,
  UserSetting.SaleCustomColumns,
  UserSetting.SaleFlattenedColumnEditability,
  UserSetting.SaleFlattenedColumnNumberPrecision,
  UserSetting.SaleFlattenedColumnOrder,
  UserSetting.SaleFlattenedColumnsEnabled,
  UserSetting.QuickFiltersStateSales,
  UserSetting.SaleColumnWidths,
  UserSetting.SalePageViewMode,
  UserSetting.SalesDefaultTab,
];

export const getSalesForEvents = async (
  viagogoVirtualIds: string[],
  filterQuery: SaleQuery | SaleQueryV2,
  useReadonlyDB: boolean,
  {
    activeAccountWebClientConfig,
    onError,
  }: {
    activeAccountWebClientConfig: PosClientConfig;
    onError?: (error: ErrorTypes) => void;
  },
  includeInternalNotes = false,
  isForSearch = false,
  isForSearchV2 = false
) => {
  const result = await tryInvokeApi(async () => {
    const client = new SaleClient(activeAccountWebClientConfig);
    const filters = {
      ...filterQuery,
      // Is is to support the legacy event ID urls
      oldPosEventIds: viagogoVirtualIds
        .filter((id) => id.startsWith('old:'))
        .map((id) => id.substring(4)),
      eventOrMappingIds: viagogoVirtualIds.filter(
        (id) => !id.startsWith('old:')
      ),
    };
    const allSales = isForSearch
      ? isForSearchV2
        ? await client.searchSalesForEventsV2(
            filters as SaleQueryV2,
            true,
            useReadonlyDB,
            includeInternalNotes
          )
        : await client.searchSalesForEvents(
            filters,
            true,
            useReadonlyDB,
            includeInternalNotes
          )
      : await client.getSalesForEvents(
          filters,
          true,
          useReadonlyDB,
          includeInternalNotes
        );

    if (isForSearchV2) {
      // Search V2 only - if we have no sales for the given event ids, return null
      // to indicate that we failed to retrieve data for all the events
      const hasAnySales = Object.values(allSales).some(
        (sales) => sales.items.length > 0
      );
      if (!hasAnySales && viagogoVirtualIds.length > 0) {
        return Promise.resolve(null);
      }
    }

    const emptyResults = viagogoVirtualIds.reduce((results, id) => {
      results[id] = {
        sales: null,
        salesSummary: null,
        listings: null,
        listingsSummary: null,
        ticketGroups: null,
        ticketGroupsSummary: null,
        failedToRetrieveData: false,
      };
      return results;
    }, {} as ExpandedEventData);

    return Promise.resolve(
      Object.entries(allSales).reduce((results, [id, sales]) => {
        results[id] = {
          sales: sales.items,
          salesSummary: sales.summary,
          listings: null,
          listingsSummary: null,
          ticketGroups: null,
          ticketGroupsSummary: null,
          failedToRetrieveData: false,
          totalItemsCount: sales.totalItemsCount,
          pitId: sales.pitId,
        };
        return results;
      }, emptyResults)
    );
  }, onError);

  if (result) {
    return Promise.resolve(result);
  }

  return Promise.resolve(
    viagogoVirtualIds.reduce((results, id) => {
      results[id] = {
        failedToRetrieveData: true,
        sales: null,
        salesSummary: null,
        listings: null,
        listingsSummary: null,
        ticketGroups: null,
        ticketGroupsSummary: null,
      };
      return results;
    }, {} as ExpandedEventData)
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const salesQueryValueTransformToUrl = (key: string, value: any) => {
  if (!value) {
    return value;
  }

  const salesKey = key as keyof SaleQuery;
  if (salesKey === 'saleDates') {
    const preset = getPresetFromUiDateTimeRange(value as DateTimeRange);
    if (preset && StandardDateRangePresetNames.includes(preset.name)) {
      return preset.name;
    }
  }

  if (salesKey === 'inHandDates') {
    const preset = getPresetFromUiDateTimeRange(value as DateTimeRange);
    if (preset && InhandDateRangePresetNames.includes(preset.name)) {
      return preset.name;
    }
  }

  return value;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const salesQueryValueTransformFromUrl = (key: string, value: any) => {
  if (!value) {
    return value;
  }

  const salesKey = key as keyof SaleQuery;
  if (salesKey === 'saleDates') {
    const preset = getUiDateTimeRangeFromPreset(
      value,
      StandardDateRangePresetNames
    );
    if (preset) {
      return preset;
    }
  } else if (salesKey === 'inHandDates') {
    const preset = getUiDateTimeRangeFromPreset(
      value,
      InhandDateRangePresetNames
    );
    if (preset) {
      return preset;
    }
  }

  return value;
};

export function SaleContextProvider({
  saveQueryInUrl,
  children,
}: {
  saveQueryInUrl?: boolean;
  children?: ReactNode;
}) {
  const { trackError } = useClientLogContext();
  const { activeAccountWebClientConfig } = useAppContext();

  const getCatalogDataExpandedCallback = useCallback(
    (ids: string[], filterQuery: SaleQuery) =>
      getSalesForEvents(
        ids,
        filterQuery,
        false,
        {
          activeAccountWebClientConfig,
          onError: (error) => {
            trackError('SaleClient.getSalesForEvents', error, {
              ...filterQuery,
              eventIds: ids,
            });
          },
        },
        true
      ),
    [activeAccountWebClientConfig, trackError]
  );

  return (
    <StopwatchLoggingContextProvider>
      <FilterQueryContextProvider<SaleQuery>
        initialQuery={DefaultSaleQuery}
        emptyQuery={EmptySaleQuery}
        saveQueryInUrl={saveQueryInUrl}
        viewModeSettingId={UserSetting.SalePageViewMode}
        queryValueTransformToUrl={salesQueryValueTransformToUrl}
        queryValueTransformFromUrl={salesQueryValueTransformFromUrl}
      >
        <CatalogDataContextProvider<SaleQuery>
          entityType={ActionOutboxEntityType.Sale}
          queryKey="getCatalogForSales"
          getCatalogDataExpanded={getCatalogDataExpandedCallback}
          transformEventData={transformData}
        >
          {children}
        </CatalogDataContextProvider>
      </FilterQueryContextProvider>
    </StopwatchLoggingContextProvider>
  );
}

export function Sales() {
  const { registerComponent } = usePageLoadStatus();

  useLayoutEffect(() => {
    registerComponent('SalesEventsExplorer');
  }, [registerComponent]);

  return (
    <>
      <SaleContextProvider saveQueryInUrl>
        <SalesRouteBody />
      </SaleContextProvider>
    </>
  );
}

export const getCatalogMetrics = (
  client: CatalogClient,
  filterQuery: SaleQuery,
  rowVersion?: number | null,
  includeTags?: boolean
) => {
  return client.getCatalogMetricsForSales(filterQuery, rowVersion, includeTags);
};

const SalesRouteBody = () => {
  const { activeAccountWebClientConfig } = useAppContext();

  const isMobile = useMatchMedia('mobile');
  const hasMobileSalePortfolioPageFeature = useUserHasFeature(
    Feature.MobileSalePortfolioPage
  );

  const getActivePosEntity = useCallback(
    async (saleId: number) => {
      const saleDetail = await new SaleClient(
        activeAccountWebClientConfig
      ).getSaleBySaleId(saleId);

      if (saleDetail) {
        return {
          posEntityId: saleDetail.id,
          posEntity: saleDetail,
          posEntityDisplayId: saleDetail.idOnMkp,
        };
      }

      return {};
    },
    [activeAccountWebClientConfig]
  );

  const hasShowEventTagOnEventTileFeature = useUserHasFeature(
    Feature.ShowEventTagOnEventTile
  );

  return (
    <CatalogMetricsContextProvider<SaleMetrics, SaleQuery>
      queryKey="getCatalogSaleMetrics"
      entityType={ActionOutboxEntityType.Sale}
      getCatalogMetrics={(c, f, r) =>
        getCatalogMetrics(c, f, r, hasShowEventTagOnEventTileFeature)
      }
      addCatalogMetrics={addSaleMetrics}
    >
      <ActivePosEntityProvider<SaleDetails>
        entityType={ActionOutboxEntityType.Sale}
        getActivePosEntity={getActivePosEntity}
      >
        {isMobile && hasMobileSalePortfolioPageFeature ? (
          <SaleEventsMobile />
        ) : (
          <SidePanelProvider sidePanelId="main-pages">
            <ActiveSortTableColumnProvider
              entityType={ActionOutboxEntityType.Sale}
              defaultSorting={[]}
            >
              <LoadingTracker componentName="SalesEventsExplorer">
                <SalesEventsExplorer />
              </LoadingTracker>
            </ActiveSortTableColumnProvider>
          </SidePanelProvider>
        )}
      </ActivePosEntityProvider>
    </CatalogMetricsContextProvider>
  );
};
